import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { api, ApiError } from "../api";
import { Table } from "../components/Table";
import type { Column } from "../components/Table";
import { Modal } from "../components/Modal";
import { MoneyInput } from "../components/MoneyInput";
import { DateInput } from "../components/DateInput";
import { fmtDate, fmtINR } from "../fmt";

interface Transaction {
  id: string;
  date: string;
  amount: number;
  drcr: "dr" | "cr";
  accountId: string;
  partyId: string | null;
  categoryId: string | null;
  narration: string | null;
  notes: string | null;
  source: string;
}

interface Account {
  id: string;
  name: string;
}
interface Party {
  id: string;
  name: string;
}
interface Category {
  id: string;
  name: string;
  kind: string;
}

interface FormState {
  date: string;
  amount: number | null;
  drcr: "dr" | "cr";
  accountId: string;
  partyId: string;
  categoryId: string;
  narration: string;
  notes: string;
}

const emptyForm: FormState = {
  date: new Date().toISOString().slice(0, 10),
  amount: null,
  drcr: "dr",
  accountId: "",
  partyId: "",
  categoryId: "",
  narration: "",
  notes: "",
};

export function Transactions() {
  const [txns, setTxns] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [filterAccount, setFilterAccount] = useState("");
  const [filterParty, setFilterParty] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<Account[]>("/api/accounts"),
      api.get<Party[]>("/api/parties"),
      api.get<Category[]>("/api/categories"),
    ])
      .then(([a, p, c]) => {
        setAccounts(a);
        setParties(p);
        setCategories(c);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load reference data"));
  }, []);

  const load = () => {
    const qs = new URLSearchParams();
    if (filterAccount) qs.set("accountId", filterAccount);
    if (filterParty) qs.set("partyId", filterParty);
    if (filterFrom) qs.set("from", filterFrom);
    if (filterTo) qs.set("to", filterTo);
    const suffix = qs.toString() ? `?${qs}` : "";
    api
      .get<Transaction[]>(`/api/transactions${suffix}`)
      .then(setTxns)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load transactions"));
  };

  useEffect(load, [filterAccount, filterParty, filterFrom, filterTo]);

  const accountName = (id: string) => accounts.find((a) => a.id === id)?.name ?? "-";
  const partyName = (id: string | null) => (id ? parties.find((p) => p.id === id)?.name ?? "-" : "-");
  const categoryName = (id: string | null) => (id ? categories.find((c) => c.id === id)?.name ?? "-" : "-");

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, accountId: accounts[0]?.id ?? "" });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (t: Transaction) => {
    setEditing(t);
    setForm({
      date: t.date,
      amount: t.amount,
      drcr: t.drcr,
      accountId: t.accountId,
      partyId: t.partyId ?? "",
      categoryId: t.categoryId ?? "",
      narration: t.narration ?? "",
      notes: t.notes ?? "",
    });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.accountId) {
      setFormError("account is required");
      return;
    }
    const body = {
      date: form.date,
      amount: form.amount ?? 0,
      drcr: form.drcr,
      accountId: form.accountId,
      partyId: form.partyId || null,
      categoryId: form.categoryId || null,
      narration: form.narration || null,
      notes: form.notes || null,
    };
    try {
      if (editing) {
        await api.patch(`/api/transactions/${editing.id}`, body);
      } else {
        await api.post("/api/transactions", body);
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "save failed");
    }
  };

  const onDelete = async (t: Transaction) => {
    if (!confirm("Delete this transaction?")) return;
    try {
      await api.delete(`/api/transactions/${t.id}`);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed");
    }
  };

  const columns: Column<Transaction>[] = [
    { key: "date", header: "Date", render: (t) => fmtDate(t.date) },
    { key: "narration", header: "Narration", render: (t) => t.narration ?? "-" },
    { key: "account", header: "Account", render: (t) => accountName(t.accountId) },
    { key: "party", header: "Party", render: (t) => partyName(t.partyId) },
    { key: "category", header: "Category", render: (t) => categoryName(t.categoryId) },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (t) => (
        <span className={`badge ${t.drcr}`}>
          {t.drcr === "dr" ? "-" : "+"}
          {fmtINR(t.amount)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (t) => (
        <span className="actions-inline">
          <button type="button" onClick={() => openEdit(t)}>
            Edit
          </button>
          <button type="button" className="danger" onClick={() => onDelete(t)}>
            Delete
          </button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <h1>Transactions</h1>
      {error && <div className="error-text">{error}</div>}

      <div className="toolbar">
        <div className="field">
          <label htmlFor="f-acc">Account</label>
          <select id="f-acc" value={filterAccount} onChange={(e) => setFilterAccount(e.target.value)}>
            <option value="">All</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="f-party">Party</label>
          <select id="f-party" value={filterParty} onChange={(e) => setFilterParty(e.target.value)}>
            <option value="">All</option>
            {parties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="f-from">From</label>
          <input id="f-from" type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="f-to">To</label>
          <input id="f-to" type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
        </div>
        <button className="primary" type="button" onClick={openAdd}>
          Add transaction
        </button>
      </div>

      <Table columns={columns} rows={txns} rowKey={(t) => t.id} emptyText="No transactions match these filters." />

      {modalOpen && (
        <Modal title={editing ? "Edit transaction" : "Add transaction"} onClose={() => setModalOpen(false)}>
          <form onSubmit={onSubmit} className="modal-body">
            <div className="form-grid">
              <div className="field">
                <label htmlFor="t-date">Date</label>
                <DateInput value={form.date} onChange={(v) => setForm({ ...form, date: v })} required />
              </div>
              <div className="field">
                <label htmlFor="t-drcr">Dr / Cr</label>
                <select
                  id="t-drcr"
                  value={form.drcr}
                  onChange={(e) => setForm({ ...form, drcr: e.target.value as "dr" | "cr" })}
                >
                  <option value="dr">Dr (money out)</option>
                  <option value="cr">Cr (money in)</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="t-amount">Amount</label>
                <MoneyInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} required />
              </div>
              <div className="field">
                <label htmlFor="t-account">Account</label>
                <select
                  id="t-account"
                  required
                  value={form.accountId}
                  onChange={(e) => setForm({ ...form, accountId: e.target.value })}
                >
                  <option value="">-- select --</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="t-party">Party</label>
                <select id="t-party" value={form.partyId} onChange={(e) => setForm({ ...form, partyId: e.target.value })}>
                  <option value="">-- none --</option>
                  {parties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="t-category">Category</label>
                <select
                  id="t-category"
                  value={form.categoryId}
                  onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                >
                  <option value="">-- none --</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.kind})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="t-narration">Narration</label>
              <input
                id="t-narration"
                value={form.narration}
                onChange={(e) => setForm({ ...form, narration: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="t-notes">Notes</label>
              <textarea id="t-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {formError && <div className="error-text">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="primary" type="submit">
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
